import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetPaymentStatusMutation } from "../../services/signup/registration";
import PaymentStatusContent from "./PaymentStatusContent";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 4000;

const PaymentPollingScreen = ({ trackingToken, isFlatPricing, onSuccess, onError }) => {
  const { t } = useTranslation("signup");
  const [status, setStatus] = useState("polling"); // polling | success | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const [triggerVerify] = useGetPaymentStatusMutation();

  const poll = async () => {
    attemptsRef.current += 1;

    if (attemptsRef.current > MAX_ATTEMPTS) {
      clearInterval(timerRef.current);
      setStatus("verification");
      return;
    }

    try {
      const result = await triggerVerify({ token: trackingToken }).unwrap();

      if (result?.invoice_details) {
        setInvoiceDetails(result.invoice_details);
      }

      if (result?.subscription_status === "completed") {
        clearInterval(timerRef.current);
        setStatus("success");
        if (onSuccess) onSuccess(result);
      } else if (!result?.subscription_status) {
        clearInterval(timerRef.current);
        const msg = result?.error || t("payment_failed", "Payment verification failed.");
        setStatus("error");
        setErrorMessage(msg);
        if (onError) onError(msg);
      }
      // else: still pending, keep polling
    } catch (err) {
      clearInterval(timerRef.current);
      const msg = t("payment_error", "An error occurred while checking payment status.");
      setStatus("error");
      setErrorMessage(msg);
      if (onError) onError(msg);
    }
  };

  useEffect(() => {
    if (!trackingToken) return;

    // Kick off immediately, then poll on interval
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => clearInterval(timerRef.current);
  }, [trackingToken]);

  return (
    <div className="payment-polling-screen-wrapper">
      <PaymentStatusContent
        status={status}
        errorMessage={errorMessage}
        invoiceDetails={invoiceDetails}
        isFlatPricing={isFlatPricing}
      />
    </div>
  );
};

export default PaymentPollingScreen;
